import React, { useEffect, useState, useRef, useCallback } from "react";
import { Rnd } from "react-rnd";

const resizeHandleCSS = `
  .react-resizable-handle {
    touch-action: none !important;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    position: absolute;
    z-index: 1000 !important;
    pointer-events: auto !important;
  }
  .react-resizable-handle-se {
    z-index: 1001 !important;
  }
  .react-resizable-handle-sw {
    z-index: 1001 !important;
  }
  .react-resizable-handle-nw {
    z-index: 1001 !important;
  }
  .react-resizable-handle-ne {
    z-index: 1001 !important;
  }
  .react-rnd {
    touch-action: pan-x pan-y !important;
    cursor: move !important;
  }
  .react-rnd:hover {
    cursor: move !important;
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('resize-handle-styles')) {
  const style = document.createElement('style');
  style.id = 'resize-handle-styles';
  style.textContent = resizeHandleCSS;
  document.head.appendChild(style);
}

const ResizeAndDraggable = ({
  defaultValue = {
    x: 150,
    y: 205,
    width: 270,
    height: 323,
  },
  minWidth = 270,
  minHeight = 323,
  children,
  aspectRatio = true,
  resize = (data) => data,
  resizeEnd,
  bounds = "window",
  onFocusValue = false,
  showAccordin,
  setDirectionResizing,
  page,
  center = true,
  ...props
}) => {
  const [dimensions, setDimensions] = useState({
    width: defaultValue?.width,
    height: defaultValue?.height,
  });
  const [minHeightChecker, setMinCheck] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [previousPosition, setPreviousPosition] = useState({
    x: Math.floor((Math.random() * (window.innerWidth - minWidth)) / 2),
    y: Math.floor((Math.random() * (window.innerHeight - minHeight)) / 2),
  });
  
  const rafRef = useRef(null);
  const lastUpdateRef = useRef({ width: defaultValue?.width, height: defaultValue?.height });
  const pendingUpdateRef = useRef(null);
  
  const { x, y, ...others } = defaultValue;

  useEffect(() => {
    resize({ width: defaultValue.width, height: defaultValue.height });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.dispatchEvent(
      new CustomEvent("draggable-widget-toggle", {
        detail: { target: "widget", open: true },
      })
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("draggable-widget-toggle", {
          detail: { target: "widget", open: false },
        })
      );
    };
  }, []);

  const isMobile = typeof navigator !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const handleSize = isMobile ? 20 : 8;
  
  const resizeHandleStyles = {
    top: { 
      cursor: "ns-resize",
      width: "100%",
      height: `${handleSize}px`,
      top: `-${handleSize / 2}px`,
      touchAction: "none",
      zIndex: 1000,
      pointerEvents: "auto",
    },
    bottom: { 
      cursor: "ns-resize",
      width: "100%",
      height: `${handleSize}px`,
      bottom: `-${handleSize / 2}px`,
      touchAction: "none",
      zIndex: 1000,
      pointerEvents: "auto",
    },
    left: { 
      cursor: "ew-resize",
      width: `${handleSize}px`,
      height: "100%",
      left: `-${handleSize / 2}px`,
      touchAction: "none",
      zIndex: 1000,
      pointerEvents: "auto",
    },
    right: { 
      cursor: "ew-resize",
      width: `${handleSize}px`,
      height: "100%",
      right: `-${handleSize / 2}px`,
      touchAction: "none",
      zIndex: 1000,
      pointerEvents: "auto",
    },
    topLeft: { 
      cursor: "nwse-resize",
      width: `${handleSize}px`,
      height: `${handleSize}px`,
      top: `-${handleSize / 2}px`,
      left: `-${handleSize / 2}px`,
      touchAction: "none",
      zIndex: 1001,
      pointerEvents: "auto",
    },
    topRight: { 
      cursor: "nesw-resize",
      width: `${handleSize}px`,
      height: `${handleSize}px`,
      top: `-${handleSize / 2}px`,
      right: `-${handleSize / 2}px`,
      touchAction: "none",
      zIndex: 1001,
      pointerEvents: "auto",
    },
    bottomLeft: { 
      cursor: "nesw-resize",
      width: `${handleSize}px`,
      height: `${handleSize}px`,
      bottom: `-${handleSize / 2}px`,
      left: `-${handleSize / 2}px`,
      touchAction: "none",
      zIndex: 1001,
      pointerEvents: "auto",
    },
    bottomRight: { 
      cursor: "nwse-resize",
      width: `${handleSize}px`,
      height: `${handleSize}px`,
      bottom: `-${handleSize / 2}px`,
      right: `-${handleSize / 2}px`,
      touchAction: "none",
      zIndex: 1001,
      pointerEvents: "auto",
    },
  };

  const generateNewPosition = () => {
    let newX, newY;
    do {
      newX = Math.floor((Math.random() * (window.innerWidth - minWidth)) / 2);
      newY = Math.floor((Math.random() * (window.innerHeight - minHeight)) / 2);
    } while (newX === previousPosition.x && newY === previousPosition.y);

    return { x: newX, y: newY };
  };

  const getInitialPosition = () => {
    const widgetWidth = defaultValue?.width || dimensions.width;
    const widgetHeight = defaultValue?.height || dimensions.height;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    if (center) {
      let x = Math.max(0, Math.floor((window.innerWidth - widgetWidth) / 2));
      let y = Math.max(0, Math.floor((window.innerHeight - widgetHeight) / 2)) - 100;
      return { x, y };
    }
    return generateNewPosition();
  };

  const initialPosition = getInitialPosition();

  const minHeightForSpecificPage = 44;

  const smoothResize = useCallback((layout) => {
    const hasChanged = 
      lastUpdateRef.current.width !== layout.width || 
      lastUpdateRef.current.height !== layout.height;
    
    if (!hasChanged) return;
    
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    lastUpdateRef.current = { 
      width: layout.width, 
      height: layout.height 
    };
    
    setDimensions(layout);
    
    rafRef.current = requestAnimationFrame(() => {
      resize(layout);
    });
  }, [resize]);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const checkMinHeight = () => {
      if (showAccordin !== undefined && !showAccordin) {
        if (page === "station" || page === "text" || page === "group") {
          setMinCheck(minHeightForSpecificPage);
          return;
        }
        setMinCheck(0);
        return minHeight;
      } else {
        setMinCheck(minHeight);
      }
    };
    checkMinHeight();
  }, [showAccordin]);

  useEffect(() => {
    const isActive = isResizing || isDragging;
    if (isActive) {
      document.body.classList.add("isDragging");
      document.body.style.userSelect = "none";
      document.body.style.WebkitUserSelect = "none";
      document.body.style.MozUserSelect = "none";
      document.body.style.msUserSelect = "none";
      if (isDragging) {
        document.body.style.cursor = "grabbing";
      }
    } else {
      document.body.classList.remove("isDragging");
      document.body.style.userSelect = "";
      document.body.style.WebkitUserSelect = "";
      document.body.style.MozUserSelect = "";
      document.body.style.msUserSelect = "";
      document.body.style.cursor = "";
    }
    
    return () => {
      document.body.classList.remove("isDragging");
      document.body.style.userSelect = "";
      document.body.style.WebkitUserSelect = "";
      document.body.style.MozUserSelect = "";
      document.body.style.msUserSelect = "";
      document.body.style.cursor = "";
    };
  }, [isResizing, isDragging]);

  const containerStyle = {
    width: "100%",
    height: "100%",
    willChange: isResizing || isDragging ? "transform" : "auto",
    transform: "translate3d(0, 0, 0)",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
    WebkitFontSmoothing: "antialiased",
    pointerEvents: "auto",
  };

  const rndStyle = {
    willChange: isResizing || isDragging ? "transform, width, height" : "auto",
    pointerEvents: "auto",
    userSelect: "none",
    ...props.style,
  };

  return (
    <Rnd
      lockAspectRatio={aspectRatio}
      default={{
        x: initialPosition.x,
        y: initialPosition.y,
        ...others,
      }}
      size={{
        height: !showAccordin ? "0px" : dimensions?.height,
        width: dimensions?.width,
      }}
      minWidth={minWidth}
      minHeight={minHeightChecker}
      bounds={bounds}
      disableDragging={onFocusValue || isResizing}
      enableUserSelectHack={false}
      cancel=".no-drag-inside-slider, button, [role='button'], .MuiIconButton-root, .MuiSwitch-root, svg, [data-no-drag], .MuiFormControlLabel-root, [class*='react-resizable-handle']"
      enableResizing={
        showAccordin
          ? {
            top: true,
            bottom: true,
            left: true,
            right: true,
            topLeft: true,
            topRight: true,
            bottomLeft: true,
            bottomRight: true,
          }
          : false
      }
      resizeHandleStyles={resizeHandleStyles}
      onDragStart={(e, d) => {
        const target = e.target;
        const isResizeHandle = target.closest('[class*="react-resizable-handle"]');
        if (isResizeHandle) {
          return;
        }
        const isInteractiveElement = 
          target.closest('button') || 
          target.closest('[role="button"]') || 
          target.closest('.MuiIconButton-root') || 
          target.closest('.MuiSwitch-root') ||
          target.closest('svg') ||
          target.closest('[data-no-drag]') ||
          target.closest('.no-drag-inside-slider') ||
          target.closest('.MuiFormControlLabel-root');
        
        if (isInteractiveElement) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        
        setIsDragging(true);
        if (props.onDragStart) {
          props.onDragStart(e, d);
        }
      }}
      onDrag={(e, d) => {
        if (props.onDrag) {
          props.onDrag(e, d);
        }
      }}
      onDragStop={(e, d) => {
        setIsDragging(false);
        if (props.onDragStop) {
          props.onDragStop(e, d);
        }
      }}
      onResize={(e, direction, ref, delta, position) => {
        if (e.touches) {
          e.preventDefault();
        }
        if (page === "clock") {
          if (
            direction === "topLeft" ||
            direction === "topRight" ||
            direction === "bottomLeft" ||
            direction === "bottomRight"
          ) {
            setDirectionResizing(true);
          } else {
            setDirectionResizing(false);
          }
        }
        const screenWidth = window.innerWidth;
        let width = parseInt(ref.style.width, 10);
        let height = parseInt(ref.style.height, 10);
        
        if (screenWidth < 520) {
          const maxWidth = screenWidth * 0.95;
          width = Math.min(width, maxWidth);
          width = Math.max(width, minWidth);
          ref.style.width = `${width}px`;
        }
        
        const layout = {
          width: width,
          height: height,
          transform: `${Math.min(
            3,
            width / minWidth,
            height / minHeight
          )}`,
        };
        smoothResize(layout);
      }}
      onResizeStart={(e, direction, ref) => {
        e.stopPropagation();
        if (e.touches) {
          e.preventDefault();
        }
        setIsResizing(true);
        if (props.onResizeStart) {
          props.onResizeStart(e, direction, ref);
        }
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        const screenWidth = window.innerWidth;
        let finalWidth = parseInt(ref.style.width, 10);
        let finalHeight = parseInt(ref.style.height, 10);
        
        if (screenWidth < 520) {
          const maxWidth = screenWidth * 0.95;
          finalWidth = Math.min(finalWidth, maxWidth);
          finalWidth = Math.max(finalWidth, minWidth);
        }
        
        const finalLayout = {
          width: finalWidth,
          height: finalHeight,
          transform: `${Math.min(
            3,
            finalWidth / minWidth,
            finalHeight / minHeight
          )}`,
        };
        setDimensions(finalLayout);
        resize(finalLayout);
        
        if (resizeEnd) {
          resizeEnd(finalLayout);
        }
        
        setIsResizing(false);
        
        if (props.onResizeStop) {
          props.onResizeStop(e, direction, ref, delta, position);
        }
      }}
      style={rndStyle}
      {...props}
    >
      <div 
        style={containerStyle}
      >
        {children}
      </div>
    </Rnd>
  );
};

export default ResizeAndDraggable;

